<template>
  <div class="company-energy-card">
    <div class="company-energy-card__main">
      {{ safeTotal }}%
    </div>

    <div class="company-energy-card__legend">
      <div
          v-for="item in normalizedLevels"
          :key="item.key"
          class="company-energy-card__legend-row"
      >
        <div class="company-energy-card__legend-name">
          <span
              class="company-energy-card__legend-mark"
              :style="{ backgroundColor: item.color }"
          ></span>

          <span>{{ item.label }}</span>
        </div>

        <span class="company-energy-card__legend-value">
          {{ item.value }} чел.
        </span>
      </div>
    </div>

    <div
        class="company-energy-card__bar"
        :class="{ 'company-energy-card__bar--empty': !hasSegments }"
    >
      <template v-if="hasSegments">
        <div
            v-for="item in barSegments"
            :key="item.key"
            class="company-energy-card__bar-part"
            :style="{
              width: item.percent + '%',
              backgroundColor: item.color
            }"
        ></div>
      </template>
    </div>

    <div
        v-if="hasSegments"
        class="company-energy-card__bar-values"
    >
      <span
          v-for="item in barSegments"
          :key="item.key"
          class="company-energy-card__bar-value"
          :style="{ width: item.percent + '%' }"
      >
        {{ item.value }}
      </span>
    </div>

    <div class="company-energy-card__chart">
      <canvas ref="chartCanvas"></canvas>
    </div>
  </div>
</template>

<script>
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
} from 'chart.js'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

Chart.register(
    LineController,
    LineElement,
    PointElement,
    LinearScale,
    CategoryScale,
    Filler,
    Tooltip,
)

export default {
  name: 'CompanyEnergyCard',

  props: {
    total: {
      type: Number,
      default: 0,
    },
    levels: {
      type: Array,
      default: () => [],
    },
    chartLabels: {
      type: Array,
      default: () => [],
    },
    chartValues: {
      type: Array,
      default: () => [],
    },
  },

  setup(props) {
    const chartCanvas = ref(null)
    let chartInstance = null

    const safeTotal = computed(() => {
      const value = Number(props.total)

      return Number.isFinite(value) ? value : 0
    })

    const normalizedLevels = computed(() => {
      return props.levels.map((item) => {
        const value = Number(item.value)

        return {
          ...item,
          value: Number.isFinite(value) ? value : 0,
        }
      })
    })

    const levelsTotal = computed(() => {
      return normalizedLevels.value.reduce((sum, item) => {
        return sum + item.value
      }, 0)
    })

    const hasSegments = computed(() => {
      return levelsTotal.value > 0
    })

    const barSegments = computed(() => {
      if (!levelsTotal.value) {
        return []
      }

      return normalizedLevels.value
          .filter(item => item.value > 0)
          .map(item => ({
            ...item,
            percent: Number(((item.value / levelsTotal.value) * 100).toFixed(2)),
          }))
    })

    const getChartGradient = (ctx, chartArea) => {
      const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)

      gradient.addColorStop(0, 'rgba(33, 172, 12, 0.32)')
      gradient.addColorStop(1, 'rgba(33, 172, 12, 0)')

      return gradient
    }

    const destroyChart = () => {
      if (chartInstance) {
        chartInstance.destroy()
        chartInstance = null
      }
    }

    const renderChart = async () => {
      await nextTick()

      if (!chartCanvas.value) {
        return
      }

      destroyChart()

      const ctx = chartCanvas.value.getContext('2d')

      chartInstance = new Chart(ctx, {
        type: 'line',
        data: {
          labels: props.chartLabels,
          datasets: [
            {
              data: props.chartValues,
              borderColor: '#21AC0C',
              backgroundColor: (context) => {
                const chart = context.chart
                const { chartArea } = chart

                if (!chartArea) {
                  return 'rgba(33, 172, 12, 0.16)'
                }

                return getChartGradient(chart.ctx, chartArea)
              },
              borderWidth: 1,
              pointRadius: 3,
              pointHoverRadius: 4,
              pointBackgroundColor: '#1C960A',
              pointBorderColor: '#1C960A',
              fill: true,
              tension: 0.25,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: true,
              displayColors: false,
              callbacks: {
                label: (context) => `${context.parsed.y}%`,
              },
            },
            datalabels: {
              display: false,
            },
          },
          scales: {
            x: {
              grid: {
                color: '#EEF0F3',
                drawTicks: false,
              },
              border: {
                display: false,
              },
              ticks: {
                color: 'rgba(0, 0, 0, 0.45)',
                font: {
                  size: 12,
                },
              },
            },
            y: {
              min: 0,
              max: 100,
              ticks: {
                stepSize: 50,
                color: 'rgba(0, 0, 0, 0.45)',
                font: {
                  size: 12,
                },
              },
              grid: {
                color: '#EEF0F3',
                drawTicks: false,
              },
              border: {
                display: false,
              },
            },
          },
        },
      })
    }

    onMounted(() => {
      renderChart()
    })

    onBeforeUnmount(() => {
      destroyChart()
    })

    watch(
        () => [props.chartLabels, props.chartValues],
        () => {
          renderChart()
        },
        {
          deep: true,
        }
    )

    return {
      chartCanvas,
      safeTotal,
      normalizedLevels,
      barSegments,
      hasSegments,
    }
  },
}
</script>

<style scoped lang="scss">
.company-energy-card {
  width: 100%;
  display: flex;
  flex-direction: column;
  text-align: start;
}

.company-energy-card__main {
  margin-bottom: 18px;
  font-weight: 600;
  font-size: 30px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.company-energy-card__legend {
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin-bottom: 18px;
}

.company-energy-card__legend-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.company-energy-card__legend-name {
  display: flex;
  align-items: center;
  gap: 10px;
}

.company-energy-card__legend-mark {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  flex: 0 0 auto;
}

.company-energy-card__legend-value {
  color: rgba(0, 0, 0, 0.88);
  text-decoration: underline;
  white-space: nowrap;
}

.company-energy-card__bar {
  display: flex;
  width: 100%;
  height: 10px;
  overflow: hidden;
  border-radius: 999px;
  gap: 2px;
}

.company-energy-card__bar--empty {
  background: #d9dee7;
}

.company-energy-card__bar-part {
  height: 100%;
  min-width: 0;
}

.company-energy-card__bar-values {
  display: flex;
  width: 100%;
  margin-top: 6px;
  font-size: 13px;
  line-height: 110%;
  color: rgba(0, 0, 0, 0.45);
}

.company-energy-card__bar-value {
  min-width: 0;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.company-energy-card__chart {
  width: 100%;
  height: 122px;
  margin-top: 18px;
}
</style>